
import { useState, useEffect } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Tooltip from "../tooltips";
import {
  LayoutDashboard,
  Users,
  FileText,
  Package,
  Settings,
  BarChart2,
  LogOut,
  Tag,
  UserPlus,
  ListChecks,
  ShoppingCart,
  Menu,
  X,
  ChevronDown,
  Calendar,
  Book,
  Brain,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

export const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const isCompanySuperAdmin = user?.role === "company_super_admin";
  const isCompanyAdmin = user?.role === "company_admin";
  const isDetailPage = location.pathname.includes("/view/") || location.pathname.includes("/edit/");

  useEffect(() => {
    // Close mobile menu when route changes
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Define navigation items based on user role
  const getNavigationItems = () => {
    if (isCompanySuperAdmin) {
      // Company Super Admin navigation items
      return [
        {
          name: "Dashboard",
          href: "/super-admin",
          icon: <LayoutDashboard className="h-5 w-5" />,
          current: location.pathname === "/super-admin",
        },
        {
          name: "Manage Admins",
          href: "/super-admin/manage-admins",
          icon: <UserPlus className="h-5 w-5" />,
          current: location.pathname === "/super-admin/manage-admins",
        },
        {
          name: "Candidates",
          href: "/super-admin/candidates",
          icon: <Users className="h-5 w-5" />,
          current: location.pathname === "/super-admin/candidates",
        },
        {
          name: "Question Banks",
          href: "/super-admin/question-banks",
          icon: <ShoppingCart className="h-5 w-5" />,
          current: location.pathname === "/super-admin/question-banks",
        },
        {
          name: "Analytics",
          href: "/super-admin/analytics",
          icon: <BarChart2 className="h-5 w-5" />,
          current: location.pathname === "/super-admin/analytics",
        },
        {
          name: "Settings",
          href: "/super-admin/settings",
          icon: <Settings className="h-5 w-5" />,
          current: location.pathname === "/super-admin/settings",
        },
      ];
    } else {
      // Company Admin navigation items
      return [
        {
          name: "Dashboard",
          href: "/admin",
          icon: <LayoutDashboard className="h-5 w-5" />,
          current: location.pathname === "/admin",
        },
        {
          name: "Questions",
          href: "/admin/question_bank",
          icon: <ListChecks className="h-5 w-5" />,
          current: location.pathname.includes("/admin/question_bank"),
        },
        {
          name: "Exams",
          href: "/admin/exams",
          icon: <FileText className="h-5 w-5" />,
          current: location.pathname.includes("/admin/exams") && !isDetailPage,
        },
        {
          name: "Candidates",
          href: "/admin/candidates",
          icon: <Users className="h-5 w-5" />,
          current: location.pathname.includes("/admin/candidates") && !isDetailPage,
        },
        {
          name: "Exam Assignment",
          href: "/admin/exam_assignment",
          icon: <Book className="h-5 w-5" />,
          current: location.pathname.includes("/admin/exam_assignment") && !isDetailPage,
        },
        {
          name: "Categories",
          href: "/admin/categories",
          icon: <Tag className="h-5 w-5" />,
          current: location.pathname === "/admin/categories",
        },
        // {
        //   name: "AI Module",
        //   href: "/admin/ai-module",
        //   icon: <Brain className="h-5 w-5" />,
        //   current: location.pathname === "/admin/ai-module",
        // },
        {
          name: "Analytics",
          href: "/admin/analytics",
          icon: <BarChart2 className="h-5 w-5" />,
          current: location.pathname === "/admin/analytics",
        },
        {
          name: "Calendar",
          href: "/admin/exam_calendar",
          icon: <Calendar className="h-5 w-5" />,
          current: location.pathname === "/admin/exam_calendar",
        },
        {
          name: "Settings",
          href: "/admin/settings",
          icon: <Settings className="h-5 w-5" />,
          current: location.pathname === "/admin/settings",
        },
      ];
    }
  };

  const navigation = getNavigationItems();


  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
    : "U";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          variant="outline"
          size="icon"
          onClick={() => setIsMobileMenuOpen(true)}
        >
          <Menu className="h-5 w-5" />
        </Button>
      </div>

      {/* Mobile Navigation */}
      <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
        <SheetContent side="left" className="p-0 w-64">
          <div className="space-y-4 py-4">
            <div className="px-3 py-2 flex items-center justify-between border-b">
              <Link to={isCompanySuperAdmin ? "/super-admin" : "/admin"} className="flex items-center">
                <h2 className="text-lg font-bold text-primary">
                  {isCompanySuperAdmin ? "Company Admin" : "Admin Portal"}
                </h2>
              </Link>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="px-3">
              {navigation.map((item) => (
                <Link key={item.name} to={item.href}>
                  <Button
                    variant={item.current ? "default" : "ghost"}
                    className="w-full justify-start mb-1"
                  >
                    {item.icon}
                    <span className="ml-2">{item.name}</span>
                  </Button>
                </Link>
              ))}
              <Button
                variant="ghost"
                className="w-full justify-start mt-4 text-red-500 hover:text-red-700 hover:bg-red-50"
                onClick={handleLogout}
              >
                <LogOut className="mr-2 h-5 w-5" />
                Logout
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Desktop Sidebar */}
       <div className="hidden lg:flex lg:w-auto lg:flex-col lg:fixed lg:inset-y-0 overflow-visible z-40">
      <div className={`flex-1 flex flex-col min-h-0 bg-white dark:bg-gray-950 border-r transition-all duration-300 ${isCollapsed ? 'w-[68px]' : 'w-64'}`}>

        {/* Top Section with Toggle Button */}
        <div className="flex items-center justify-between px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => setIsCollapsed(!isCollapsed)}>
            <Menu className="h-5 w-5" />
          </Button>
          {!isCollapsed && (
            <Link to={isCompanySuperAdmin ? "/super-admin" : "/admin"} className="ml-2">
              <h1 className="text-lg font-bold text-primary">
                {isCompanySuperAdmin ? "Company Admin" : "Admin Portal"}
              </h1>
            </Link>
          )}
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 px-2">
          <nav className="flex-1 space-y-1">
            {navigation.map((item) => (
              <Link key={item.name} to={item.href}>
                <Button
                  variant={item.current ? "default" : "ghost"}
                  className="w-full justify-start mb-1"
                >
                  {isCollapsed ? (
                    <Tooltip text={item.name}>{item.icon}</Tooltip>
                  ) : (
                    <>
                      {item.icon}
                      <span className="ml-3">{item.name}</span>
                    </>
                  )}
                </Button>
              </Link>
            ))}
          </nav>
        </ScrollArea>

        {/* User Profile Section */}
        <div className="border-t p-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={user?.profileImage} />
              <AvatarFallback className="bg-primary text-primary-foreground">
                {initials}
              </AvatarFallback>
            </Avatar>
            {!isCollapsed && (
              <>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {user?.email}
                  </p>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>My Account</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to={isCompanySuperAdmin ? "/super-admin/settings" : "/admin/settings"}>
                        Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleLogout} className="text-red-500">
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        </div>
      </div>
    </div>

      {/* Main Content */}
      <div className={isCollapsed ? "lg:pl-14" : "lg:pl-64"}>
        <main className="py-6">
          <div className="mx-auto px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};
